import type { AuthProviderId } from '@gmp/shared-types/browser';
import { SERVER_URL } from './config';
import { adoptAccount, forgetAccount, getCredentials, type AccountCredentials } from './identity';
import { party } from './client';
import { getPrefs } from '../app/prefs';

/**
 * Optional sign-in (docs/ACCOUNTS.md). Every call carries this device's current credential, so the server can
 * link a first sign-in to the guest playing here (parties carry over) and bind provider sign-ins to this browser.
 */
type Reply<T> = { ok: true; value: T } | { ok: false; error: string };

async function call<T>(path: string, body: Record<string, unknown> = {}): Promise<Reply<T>> {
  try {
    const creds = await getCredentials();
    const res = await fetch(`${SERVER_URL}${path}`, {
      method: 'POST',
      headers: { authorization: `Bearer ${creds.token}`, 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = (await res.json().catch(() => ({}))) as T & { error?: string };
    return res.ok ? { ok: true, value: json } : { ok: false, error: json.error ?? `Something went wrong (${res.status})` };
  } catch {
    return { ok: false, error: 'No connection to the game server. Try again.' };
  }
}

/** The nickname and character a new account starts with (what this device already plays as). */
function profileDefaults() {
  const p = getPrefs();
  return { nickname: p.nickname.trim() || undefined, avatar: p.avatar };
}

/** Signed in (or out): reconnect with the new identity and reload the social view. */
async function switched(c: AccountCredentials | null) {
  if (c) adoptAccount(c);
  else await forgetAccount();
  party.resetIdentity();
}

/** Google / Facebook / Grudge Match Games: go to the provider (the browser comes back to /auth/done). */
export async function beginSignIn(provider: Exclude<AuthProviderId, 'email'>, returnTo = window.location.pathname): Promise<string | null> {
  const r = await call<{ url: string }>('/api/auth/begin', { provider, returnTo, ...profileDefaults() });
  if (!r.ok) return r.error;
  window.location.assign(r.value.url);
  return null;
}

export function emailStart(email: string) {
  return call<{ devCode?: string }>('/api/auth/email/start', { email });
}

export async function emailVerify(email: string, code: string): Promise<string | null> {
  const r = await call<AccountCredentials>('/api/auth/email/verify', { email, code, ...profileDefaults() });
  if (!r.ok) return r.error;
  await switched(r.value);
  return null;
}

/** Finishes a provider sign-in from the one-time handoff code in /auth/done#h=…. */
export async function redeem(handoff: string): Promise<string | null> {
  const r = await call<AccountCredentials>('/api/auth/redeem', { handoff });
  if (!r.ok) return r.error;
  adoptAccount(r.value);
  return null;
}

export async function signOut() {
  await switched(null);
}

export async function signOutEverywhere(): Promise<string | null> {
  const r = await call<AccountCredentials>('/api/auth/signout-all');
  if (!r.ok) return r.error;
  await switched(null);
  return null;
}

/** Deletes the account for good. This device keeps playing as the same player, as a guest. */
export async function deleteAccount(): Promise<string | null> {
  const r = await call<{ ok: true }>('/api/auth/delete', { confirm: 'DELETE' });
  if (!r.ok) return r.error;
  await forgetAccount(true);
  party.resetIdentity();
  return null;
}

export async function unlinkLogin(provider: AuthProviderId): Promise<string | null> {
  const r = await call<{ ok: true }>('/api/auth/unlink', { provider });
  if (!r.ok) return r.error;
  void party.social({ op: 'get' });
  return null;
}
